import type { FetchData } from '@hooks/useDataFetcher';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { Pagination } from 'types/core';
import type {
  ProductSubTypeInformation,
  SubTypeListResponseV1,
} from 'types/product-catalog';
import type {
  IProduct,
  ProductResponse,
  ProductsResponse,
} from 'types/prop-types/models/products';

import type { SnackbarActions } from './useSnackbar';

interface IState {
  subTypes: {
    list: Array<ProductSubTypeInformation>;
  };
  products: {
    list: IProduct[];
    pagination?: Pagination;
  };
}

export interface IProductFilters {
  type: string;
  tags?: string;
  limit: number;
  market: string;
  after?: string;
  validAt: string;
  subType: string;
}

interface IActions {
  getSubTypes: () => Promise<ProductSubTypeInformation[]>;
  getProducts: (
    filters: IProductFilters,
  ) => Promise<ProductsResponse | undefined>;
  getProductById: (productId: string) => Promise<IProduct | undefined>;
}

export type UseProducts = (
  fetchData: FetchData,
  snackbar: SnackbarActions,
) => [IState & { loading: boolean }, IActions];

const normalizeQueryValue = (value?: number | string | null): string => {
  if (value === undefined) {
    return '__undefined__';
  }

  if (value === null) {
    return '__null__';
  }

  if (value === '') {
    return '__empty__';
  }

  return String(value);
};

const toQueryStringValue = (value?: number | string): string | undefined =>
  value === undefined || value === null ? undefined : String(value);

const getErrorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error ? err.message : typeof err === 'string' ? err : fallback;

export const useProducts: UseProducts = (fetchData, snackbar) => {
  const inFlightRequests = useRef<Map<string, Promise<unknown>>>(new Map());
  const [pendingActions, setPendingActions] = useState<Set<string>>(new Set());
  const [state, setState] = useState<IState>({
    subTypes: {
      list: [],
    },
    products: {
      list: [],
    },
  });

  const startLoading = useCallback((actionKey: string) => {
    setPendingActions((prev) => {
      if (prev.has(actionKey)) {
        return prev;
      }

      return new Set(prev).add(actionKey);
    });
  }, []);

  const stopLoading = useCallback(
    (actionKey: string) =>
      setPendingActions((prev) => {
        if (!prev.has(actionKey)) {
          return prev;
        }

        const nextPendingActions = new Set(prev);
        nextPendingActions.delete(actionKey);
        return nextPendingActions;
      }),
    [],
  );

  const runWithDedup = useCallback(
    async <T>(actionKey: string, request: () => Promise<T>): Promise<T> => {
      const existingRequest = inFlightRequests.current.get(actionKey) as
        | Promise<T>
        | undefined;

      if (existingRequest) {
        return existingRequest;
      }

      startLoading(actionKey);

      const requestPromise = request().finally(() => {
        inFlightRequests.current.delete(actionKey);
        stopLoading(actionKey);
      }) as Promise<T>;

      inFlightRequests.current.set(actionKey, requestPromise);

      return requestPromise;
    },
    [startLoading, stopLoading],
  );

  const getSubTypes = useCallback<IActions['getSubTypes']>(async () => {
    if (state.subTypes.list.length) {
      return state.subTypes.list;
    }

    const actionKey = 'getSubTypes';

    try {
      return await runWithDedup(actionKey, async () => {
        const res = await fetchData<SubTypeListResponseV1>(
          '/api/products/sub-types',
        );

        const subTypes = (res.data ?? [])
          .map((item) => ({
            ...item,
            subTypes: item.subTypes?.sort() ?? [],
          }))
          .sort((a, b) =>
            (a.productType ?? '').localeCompare(b.productType ?? ''),
          );

        setState((prev) => ({ ...prev, subTypes: { list: subTypes } }));
        return subTypes;
      });
    } catch (err: unknown) {
      snackbar.error(
        getErrorMessage(
          err,
          'An error occurred while fetching product sub-types.',
        ),
      );
      return state.subTypes.list;
    }
  }, [fetchData, runWithDedup, snackbar, state.subTypes.list]);

  const getProducts = useCallback<IActions['getProducts']>(
    async (filters) => {
      const actionKey = `getProducts|type=${normalizeQueryValue(filters.type)}|subType=${normalizeQueryValue(filters.subType)}|market=${normalizeQueryValue(filters.market)}|validAt=${normalizeQueryValue(filters.validAt)}|after=${normalizeQueryValue(filters.after)}|limit=${normalizeQueryValue(filters.limit)}|tags=${normalizeQueryValue(filters.tags)}`;

      try {
        return await runWithDedup(actionKey, async () => {
          const response = await fetchData<ProductsResponse>('/api/products', {
            type: toQueryStringValue(filters.type),
            tags: toQueryStringValue(filters.tags),
            limit: toQueryStringValue(filters.limit),
            after: toQueryStringValue(filters.after),
            market: toQueryStringValue(filters.market),
            validAt: toQueryStringValue(filters.validAt),
            subType: toQueryStringValue(filters.subType),
          });

          if (response?.data !== undefined && !Array.isArray(response.data)) {
            throw new Error(
              'Malformed response: expected data to be an array.',
            );
          }

          const products = response?.data ?? [];
          setState((prev) => ({
            ...prev,
            products: { list: products, pagination: response?.pagination },
          }));
          return response ?? undefined;
        });
      } catch (err: unknown) {
        snackbar.error(
          getErrorMessage(err, 'An error occurred while fetching products.'),
        );
        return undefined;
      }
    },
    [fetchData, runWithDedup, snackbar],
  );

  const getProductById = useCallback<IActions['getProductById']>(
    async (productId) => {
      const actionKey = `getProductById|productId=${normalizeQueryValue(productId)}`;

      try {
        return await runWithDedup(actionKey, async () => {
          const res = await fetchData<ProductResponse>(
            `/api/products/${productId}`,
          );

          return res.data;
        });
      } catch (err: unknown) {
        snackbar.error(
          getErrorMessage(err, 'An error occurred while fetching the product.'),
        );
        return undefined;
      }
    },
    [fetchData, runWithDedup, snackbar],
  );

  const actions = useMemo<IActions>(
    () => ({ getSubTypes, getProducts, getProductById }),
    [getSubTypes, getProducts, getProductById],
  );

  return [{ ...state, loading: pendingActions.size > 0 }, actions];
};
